import axiosInstance from '../../../config/axios';
import { parseLocaleNumber, formatFixed } from '../../../utils/numberFormatting';

export type InvoiceKind = 'FULL' | 'DEPOSIT';
export type InvoiceStatus = 'DRAFT' | 'SENT' | 'PAID' | 'CANCELLED';
export type InvoiceLineKind = 'ITEM' | 'TEXT' | 'SUBTOTAL';

export interface InvoiceLine {
  id?: number;
  position: number;
  kind: InvoiceLineKind;
  catalog_item?: number | null;
  description: string;
  quantity: string;
  unit: string;
  unit_price_net: string;
  discount_percent: string;
  tax_rate: string;
  line_total_net?: string;
  line_total_gross?: string;
}

export interface Invoice {
  id: number;
  partner: number;
  partner_name: string;
  project: number | null;
  project_name?: string | null;
  offer: number | null;
  offer_number?: string | null;
  kind: InvoiceKind;
  status: InvoiceStatus;
  invoice_number: string;
  title: string;
  issue_date: string;
  due_date: string;
  payment_terms_days: number;
  net_total: string;
  tax_total: string;
  gross_total: string;
  currency: string;
  notes_internal?: string;
  lines: InvoiceLine[];
  created_at: string;
  updated_at: string;
}

export interface InvoiceListParams {
  partner_id?: number;
  project_id?: number;
  offer_id?: number;
  status?: string;
  kind?: InvoiceKind;
  issue_from?: string;
  issue_to?: string;
  search?: string;
  page?: number;
  page_size?: number;
  ordering?: string;
}

export interface CreateInvoicePayload {
  partner: number;
  project?: number | null;
  offer?: number | null;
  kind: InvoiceKind;
  status?: InvoiceStatus;
  invoice_number?: string;
  title: string;
  issue_date: string;
  due_date: string;
  payment_terms_days?: number;
  currency?: string;
  notes_internal?: string;
  lines: {
    position: number;
    kind: InvoiceLineKind;
    catalog_item?: number | null;
    description: string;
    quantity: number;
    unit: string;
    unit_price_net: number;
    discount_percent?: number;
    tax_rate?: number;
  }[];
}

export interface UpdateInvoicePayload extends Partial<Omit<CreateInvoicePayload, 'partner'>> {}

export interface InvoiceForm {
  partnerId: number;
  projectId?: number | null;
  offerId?: number | null;
  kind: InvoiceKind;
  status: InvoiceStatus;
  invoiceNumber?: string;
  title: string;
  issueDate: string;
  dueDate: string;
  paymentTermsDays: number;
  currency: string;
  internalNotes?: string;
  lines: {
    kind: InvoiceLineKind;
    catalogItemId?: number | null;
    description: string;
    quantity: string;
    unit: string;
    unit_price: string;
    discount_percent: string;
    tax_rate: string;
  }[];
}

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export interface SendEmailPayload {
  recipient_email: string;
  subject: string;
  message: string;
  send_as_me?: boolean;
}

export interface BulkStatusChangeResult {
  id: number;
  success: boolean;
  error?: string;
}

export interface BulkStatusChangeResponse {
  status: InvoiceStatus;
  updated_count: number;
  total_requested: number;
  results: BulkStatusChangeResult[];
}

export interface InvoiceSummary {
  DRAFT: number;
  SENT: number;
  PAID: number;
  CANCELLED: number;
  total: number;
}

export interface MonthlyPoint {
  month: string;
  label: string;
  invoiced_gross: string;
  paid_gross: string;
  count: number;
}

export const INVOICE_STATUS_OPTIONS: { value: InvoiceStatus; label: string }[] = [
  { value: 'DRAFT', label: 'Draft' },
  { value: 'SENT', label: 'Sent' },
  { value: 'PAID', label: 'Paid' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

export const INVOICE_KIND_OPTIONS: { value: InvoiceKind; label: string }[] = [
  { value: 'FULL', label: 'Final' },
  { value: 'DEPOSIT', label: 'Deposit' },
];

const BASE = '/api/admin/invoices/';

function toBackendInvoicePayload(form: InvoiceForm): CreateInvoicePayload {
  return {
    partner: form.partnerId,
    project: form.projectId ?? null,
    offer: form.offerId ?? null,
    kind: form.kind,
    status: form.status,
    invoice_number: form.invoiceNumber,
    title: form.title,
    issue_date: form.issueDate,
    due_date: form.dueDate,
    payment_terms_days: form.paymentTermsDays,
    currency: form.currency,
    notes_internal: form.internalNotes,
    lines: form.lines.map((line, idx) => ({
      position: idx + 1,
      kind: line.kind,
      catalog_item: line.catalogItemId ?? null,
      description: line.description,
      quantity: parseLocaleNumber(line.quantity) ?? 0,
      unit: line.unit,
      unit_price_net: parseLocaleNumber(line.unit_price) ?? 0,
      discount_percent: parseLocaleNumber(line.discount_percent) ?? 0,
      tax_rate: parseLocaleNumber(line.tax_rate) ?? 19,
    })),
  };
}

export default class InvoiceService {
  static async list(params?: InvoiceListParams): Promise<Invoice[]> {
    const queryParams: Record<string, string> = {};

    if (params?.partner_id) queryParams.partner_id = String(params.partner_id);
    if (params?.project_id) queryParams.project_id = String(params.project_id);
    if (params?.offer_id) queryParams.offer_id = String(params.offer_id);
    if (params?.status) queryParams.status = params.status;
    if (params?.kind) queryParams.kind = params.kind;
    if (params?.issue_from) queryParams.issue_from = params.issue_from;
    if (params?.issue_to) queryParams.issue_to = params.issue_to;
    if (params?.search) queryParams.search = params.search;
    if (params?.page) queryParams.page = String(params.page);
    if (params?.page_size) queryParams.page_size = String(params.page_size);
    if (params?.ordering) queryParams.ordering = params.ordering;

    const query = new URLSearchParams(queryParams).toString();
    const url = query ? `${BASE}?${query}` : BASE;

    const response = await axiosInstance.get<Invoice[] | PaginatedResponse<Invoice>>(url);

    if (Array.isArray(response.data)) {
      return response.data;
    }
    return response.data.results;
  }

  static async get(id: number): Promise<Invoice> {
    const response = await axiosInstance.get<Invoice>(`${BASE}${id}/`);
    return response.data;
  }

  static async create(payload: CreateInvoicePayload): Promise<Invoice> {
    const response = await axiosInstance.post<Invoice>(BASE, payload);
    return response.data;
  }

  static async createFromForm(form: InvoiceForm): Promise<Invoice> {
    const payload = toBackendInvoicePayload(form);
    return this.create(payload);
  }

  static async update(id: number, payload: UpdateInvoicePayload): Promise<Invoice> {
    const response = await axiosInstance.patch<Invoice>(`${BASE}${id}/`, payload);
    return response.data;
  }

  static async delete(id: number): Promise<void> {
    await axiosInstance.delete(`${BASE}${id}/`);
  }

  static async changeStatus(id: number, status: InvoiceStatus): Promise<Invoice> {
    const response = await axiosInstance.post<Invoice>(`${BASE}${id}/change-status/`, { status });
    return response.data;
  }

  static async bulkChangeStatus(ids: number[], status: InvoiceStatus): Promise<BulkStatusChangeResponse> {
    const response = await axiosInstance.post<BulkStatusChangeResponse>(
      `${BASE}bulk-change-status/`,
      { ids, status }
    );
    return response.data;
  }

  static async getOverdue(): Promise<Invoice[]> {
    const response = await axiosInstance.get<Invoice[] | PaginatedResponse<Invoice>>(`${BASE}overdue/`);
    if (Array.isArray(response.data)) {
      return response.data;
    }
    return response.data?.results ?? [];
  }

  static async getSummary(): Promise<InvoiceSummary> {
    const response = await axiosInstance.get<InvoiceSummary>(`${BASE}summary/`);
    return response.data;
  }

  static async getMonthlySummary(months: number = 6): Promise<MonthlyPoint[]> {
    const response = await axiosInstance.get<MonthlyPoint[]>(`${BASE}monthly-summary/?months=${months}`);
    return response.data;
  }

  static async renderPdf(id: number): Promise<{ pdf_url: string }> {
    const response = await axiosInstance.post<{ pdf_url: string }>(`${BASE}${id}/render/`);
    return response.data;
  }

  static async downloadPdf(id: number): Promise<Blob> {
    const response = await axiosInstance.get(`${BASE}${id}/pdf/`, {
      responseType: 'blob',
    });
    return response.data;
  }

  static async sendEmail(id: number, payload: SendEmailPayload): Promise<void> {
    await axiosInstance.post(`${BASE}${id}/send-email/`, payload);
  }

  static getStatusLabel(status: InvoiceStatus): string {
    const option = INVOICE_STATUS_OPTIONS.find((opt) => opt.value === status);
    return option?.label || status;
  }

  static getStatusColor(status: InvoiceStatus): { bg: string; text: string } {
    switch (status) {
      case 'DRAFT':
        return { bg: '#f3f4f6', text: '#374151' };
      case 'SENT':
        return { bg: '#dbeafe', text: '#1e40af' };
      case 'PAID':
        return { bg: '#d1fae5', text: '#065f46' };
      case 'CANCELLED':
        return { bg: '#fee2e2', text: '#991b1b' };
      default:
        return { bg: '#f3f4f6', text: '#374151' };
    }
  }

  static getKindLabel(kind: InvoiceKind): string {
    const option = INVOICE_KIND_OPTIONS.find((opt) => opt.value === kind);
    return option?.label || kind;
  }

  static formatCurrency(amount: number | string): string {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
    }).format(num);
  }
}
